import { useContext, useState } from "react";
import { AppContext } from "../App";
import api from "./api/api";
import CustomersPopUp from "./CustomersPopUp";
import FormGroup from "./form/FormGroup";
import areaCodes from "../data/areaCodes.json";

function CustomerForm({ customer }) {
	const { setPopup, setLoading, setConfirm, setSearchTerm, setSearchAddressTerm, cart, apiSettings } =
		useContext(AppContext);
	const defaultItalyAreaCode =
		areaCodes.find((code) => code.dial_code === "+39" && code.code === "IT")?.dial_code || "+39";
	const [name, setName] = useState(customer ? customer.name : "");
	const [surname, setSurname] = useState(customer ? customer.surname : "");
	const [email, setEmail] = useState(customer ? customer.email : "");
	const [areaCode, setAreaCode] = useState(
		customer ? customer.area_code || defaultItalyAreaCode : defaultItalyAreaCode
	);
	const [mobile, setMobile] = useState(customer ? customer.mobile : "");
	const [birthDate, setBirthDate] = useState(customer ? customer.birth_date : "");
	const normalizedGender =
		customer?.gender == "m" ? "uomo" : customer?.gender == "f" ? "donna" : customer?.gender || "";
	const [gender, setGender] = useState(normalizedGender);
	const [job, setJob] = useState(customer ? customer.job || "" : "");
	const isBirthDateRequired = apiSettings?.customers?.birth_date_required == 1;

	const deleteCustomer = () => {
		setConfirm({
			title: "Elimina cliente",
			message: "Sei sicuro di voler eliminare questo cliente?",
			onConfirm: () => {
				setConfirm({
					title: "Operazione irreversibile",
					message:
						"Questa operazione è irreversibile e cancellerà tutti i dati del cliente. Confermi?",
					onConfirm: async () => {
						setLoading(true);
						const res = await api.postProtected("/customers/delete/" + customer.id + "/");
						setLoading(false);
						if (res.success == 0) {
							setConfirm({ title: "Errore", message: res.error });
						} else {
							setPopup({
								title: "Cliente",
								content: <CustomersPopUp />,
							});
						}
					},
				});
			},
		});
	};

	const saveCustomer = async () => {
		let validationError = "";
		if (isBirthDateRequired && !birthDate) {
			validationError += "Il campo Data di nascita è obbligatorio\n";
		}
		if (apiSettings?.customers?.gender_required == 1 && apiSettings?.customers?.show_gender == 1 && !gender) {
			validationError += "Il campo Genere è obbligatorio\n";
		}
		if (apiSettings?.customers?.job_required == 1 && apiSettings?.customers?.show_job == 1 && !job) {
			validationError += "Il campo Professione è obbligatorio\n";
		}
		if (validationError) {
			setConfirm({ title: "Errore", message: validationError.trim() });
			return;
		}
		setLoading(true);
		if (customer) {
			const res = await api.postProtected("/customers/edit/" + customer.id + "/", {
				name: name,
				surname: surname,
				email: email,
				mobile: mobile,
				area_code: areaCode,
				birth_date: birthDate,
				gender: gender,
				job: job,
			});
			setPopup({
				title: "Cliente",
				content: <CustomersPopUp />,
			});
		} else {
			const res = await api.post("/customers/add/", {
				name: name,
				surname: surname,
				email: email,
				mobile: mobile,
				area_code: areaCode,
				birth_date: birthDate,
				gender: gender,
				job: job,
			});
			if (res.success == 0) {
				setLoading(false);
				setConfirm({
					title: "Errore",
					message: res.error,
				});
			} else {
				if (cart.delivery_type.id == "delivery") setSearchAddressTerm(surname);
				else setSearchTerm(surname);
				setPopup({
					title: "Cliente",
					content: <CustomersPopUp />,
				});
			}
		}
	};

	return (
		<div className="customer-form-flow">
			<div className="customer-form-subtitle">Compila i campi per salvare l'anagrafica cliente</div>
			<div className="customer-form-grid form">
				<FormGroup label="Nome" value={name} onChange={(e) => setName(e.target.value)} />
				<FormGroup
					label="Cognome"
					value={surname}
					onChange={(e) => setSurname(e.target.value)}
				/>
				<FormGroup
					type="email"
					label="Email"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
				/>
				<FormGroup
					type="container"
					childs={[
						<FormGroup
							label="Prefisso"
							value={areaCode}
							onChange={(e) => setAreaCode(e.target.value)}
							type="select"
							options={areaCodes}
							optionValue="dial_code"
							optionLabel={["dial_code", "code"]}
						/>,
						<FormGroup
							label="Cellulare"
							value={mobile}
							onChange={(e) => setMobile(e.target.value)}
						/>,
					]}
				/>
				<FormGroup
					label="Data di nascita"
					type="date"
					value={birthDate}
					onChange={(e) => setBirthDate(e.target.value)}
					required={isBirthDateRequired}
				/>
				{apiSettings?.customers?.show_gender == 1 && (
					<FormGroup
						type="select"
						label={"Genere" + (apiSettings?.customers?.gender_required == 1 ? "*" : "")}
						value={gender}
						onChange={(e) => setGender(e.target.value)}
						options={[
							{ name: "", id: "" },
							{ name: "Uomo", id: "uomo" },
							{ name: "Donna", id: "donna" },
							{ name: "Altro", id: "altro" },
						]}
					/>
				)}
				{apiSettings?.customers?.show_job == 1 && (
					<FormGroup
						type="select"
						label={"Professione" + (apiSettings?.customers?.job_required == 1 ? "*" : "")}
						value={job}
						onChange={(e) => setJob(e.target.value)}
						options={[
							{ name: "", id: "" },
							{ name: "Lavoratore", id: "work" },
							{ name: "Studente", id: "student" },
							{ name: "Altro", id: "other" },
						]}
					/>
				)}
			</div>
			<div className="customer-form-actions">
				{customer && (
					<button
						className="button button-danger"
						onClick={deleteCustomer}
						style={{ marginRight: "auto" }}
					>
						Elimina
					</button>
				)}
				<button
					className="button button-3"
					onClick={() =>
						setPopup({
							title: "Cliente",
							content: <CustomersPopUp />,
						})
					}
				>
					Annulla
				</button>
				<button className="button button-2" onClick={saveCustomer}>
					Salva
				</button>
			</div>
		</div>
	);
}

export default CustomerForm;
